import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const CoatingReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const coatingMeasurements = measurements.filter((measurement) => measurement.reportType === 'COATING');
  const [activeCoatingId, setActiveCoatingId] = useState<string | null>(null);
  useEffect(() => {
    if (!coatingMeasurements.length) {
      setActiveCoatingId(null);
      return;
    }

    if (!activeCoatingId || !coatingMeasurements.some((measurement) => measurement.id === activeCoatingId)) {
      setActiveCoatingId(coatingMeasurements[0].id);
    }
  }, [activeCoatingId, coatingMeasurements]);
  const activeCoating = coatingMeasurements.find((measurement) => measurement.id === activeCoatingId) || coatingMeasurements[0];
  const coatingType = activeCoating?.coatingType || '3LPE';
  const effectiveFrequency = metadata?.frequency || frequency || 'per_pipe';
  const dftMinLimit = getProjectLimitMin(projectLimitFields, 'dft_min_um', 2700);
  const holidayVoltageMin = getProjectLimitMin(projectLimitFields, 'holiday_voltage_kv', 15);
  const holidayVoltageMax = getProjectLimitMax(projectLimitFields, 'holiday_voltage_kv', 15);
  const adhesionMin = getProjectLimitMin(projectLimitFields, 'adhesion_n_cm', 100);
  const cdRadiusMax = getProjectLimitMax(projectLimitFields, 'cd_radius_mm', 8);
  const anchorProfileMin = getProjectLimitMin(projectLimitFields, 'anchor_profile_um', 60);
  const anchorProfileMax = getProjectLimitMax(projectLimitFields, 'anchor_profile_um', 100);
  const dftLimitText = getProjectLimitText(projectLimitFields, 'dft_min_um', `>= ${dftMinLimit} um`);
  const holidayVoltageLimitText = getProjectLimitText(projectLimitFields, 'holiday_voltage_kv', holidayVoltageMin === holidayVoltageMax ? `${holidayVoltageMin} kV` : `${holidayVoltageMin} - ${holidayVoltageMax} kV`);
  const adhesionLimitText = getProjectLimitText(projectLimitFields, 'adhesion_n_cm', `>= ${adhesionMin} N/cm`);
  const cdRadiusLimitText = getProjectLimitText(projectLimitFields, 'cd_radius_mm', `<= ${cdRadiusMax} mm`);
  const anchorProfileLimitText = getProjectLimitText(projectLimitFields, 'anchor_profile_um', `${anchorProfileMin} - ${anchorProfileMax} um`);
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['DFT min', dftLimitText, true],
    ['Holiday V', holidayVoltageLimitText, true],
    ['Adhesion', adhesionLimitText, true],
    ['CD radius', cdRadiusLimitText, true],
    ['Anchor profile', anchorProfileLimitText, true],
  ]);
  const getCoatingNumber = (value?: string) => {
    const parsed = Number(value);
    return value !== undefined && value !== '' && Number.isFinite(parsed) ? parsed : Number.NaN;
  };
  const expected = {
    dftMinUm: getCoatingNumber(activeCoating?.coatingDftMinUm),
    dftAvgUm: getCoatingNumber(activeCoating?.coatingDftAvgUm),
    adhesionNcm: getCoatingNumber(activeCoating?.coatingAdhesionNcm),
    cdRadiusMm: getCoatingNumber(activeCoating?.coatingCdRadiusMm),
    anchorProfileUm: getCoatingNumber(activeCoating?.coatingAnchorProfileUm),
    holidayVoltageKv: getCoatingNumber(activeCoating?.coatingHolidayVoltageKv),
    holidaysFound: getCoatingNumber(activeCoating?.coatingHolidaysFound),
    cutbackLengthMm: getCoatingNumber(activeCoating?.coatingCutbackLengthMm),
    surfacePrepGrade: activeCoating?.coatingSurfacePrepGrade || '-',
  };
  const resultRows = [
    { label: 'DFT - minimum', formula: dftLimitText, value: expected.dftMinUm, unit: 'um', min: Math.max(0, dftMinLimit * 0.8), max: Math.max(dftMinLimit * 1.5, expected.dftMinUm), lo: dftMinLimit },
    { label: 'DFT - average', formula: dftLimitText, value: expected.dftAvgUm, unit: 'um', min: Math.max(0, dftMinLimit * 0.8), max: Math.max(dftMinLimit * 1.5, expected.dftAvgUm), lo: dftMinLimit },
    { label: 'Adhesion (peel)', formula: adhesionLimitText, value: expected.adhesionNcm, unit: 'N/cm', min: Math.max(0, adhesionMin * 0.8), max: Math.max(adhesionMin * 1.5, expected.adhesionNcm), lo: adhesionMin },
    { label: 'Cathodic disbondment', formula: cdRadiusLimitText, value: expected.cdRadiusMm, unit: 'mm', min: 0, max: Math.max(cdRadiusMax * 1.2, expected.cdRadiusMm), hi: cdRadiusMax },
    { label: 'Anchor profile', formula: anchorProfileLimitText, value: expected.anchorProfileUm, unit: 'um', min: Math.max(0, anchorProfileMin * 0.8), max: Math.max(anchorProfileMax * 1.2, expected.anchorProfileUm), lo: anchorProfileMin, hi: anchorProfileMax },
  ];
  const rowStatus = (row: typeof resultRows[number]) => {
    if (!activeCoating) return 'PENDING_REVIEW';
    if (!Number.isFinite(row.value)) return 'PENDING_REVIEW';
    if (row.lo != null && row.value < row.lo) return 'FAIL';
    if (row.hi != null && row.value > row.hi) return 'FAIL';
    return 'PASS';
  };
  const overallStatus = activeCoating
    ? resultRows.some((row) => rowStatus(row) === 'FAIL') || (Number.isFinite(expected.holidaysFound) && expected.holidaysFound > 0) ? 'FAIL' : resultRows.some((row) => rowStatus(row) === 'PENDING_REVIEW') ? 'PENDING_REVIEW' : 'PASS'
    : 'PENDING_REVIEW';
  const layers = coatingType === '3LPE'
    ? [
      { name: 'Steel substrate', color: '#6F7782', height: 24 },
      { name: 'FBE epoxy primer (~ 150 um)', color: '#A6763A', height: 14 },
      { name: 'Adhesive copolymer (~ 250 um)', color: '#C39A52', height: 16 },
      { name: 'PE topcoat (~ 2700 um)', color: '#3D7E58', height: 30 },
    ]
    : [
      { name: 'Steel substrate', color: '#6F7782', height: 24 },
      { name: `${coatingType} coating`, color: '#A6763A', height: 30 },
    ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Coating system</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">{activeCoating ? `Selected ${activeCoating.coatingType || 'coating'} result` : 'No coating result saved yet'}</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('COATING')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Result
          </Button>
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-4">
            <div className="relative h-[140px] overflow-hidden rounded-lg border border-[#E2E5EA] bg-page-bg">
              <div className="absolute bottom-3 left-4 top-3 w-[74px] bg-[repeating-linear-gradient(45deg,#EEF0F3,#EEF0F3_4px,#DDE3EA_4px,#DDE3EA_8px)]" />
              <div className="absolute left-5 top-2 font-mono text-[10px] text-heritage-grey">{Number.isFinite(expected.cutbackLengthMm) ? expected.cutbackLengthMm : '-'} mm cutback</div>
              {layers.map((layer, index) => {
                const top = 18 + layers.slice(0, index).reduce((sum, item) => sum + item.height + 7, 0);
                return (
                  <div key={layer.name} className="absolute left-[92px] right-[210px] rounded-sm" style={{ top, height: layer.height, background: layer.color }}>
                    <span className="absolute left-[calc(100%+14px)] top-1 whitespace-nowrap font-mono text-[11px] text-text-grey">{layer.name}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] text-heritage-grey">
              Type: <span className="font-mono text-text-grey">{coatingType}</span> · cutback <span className="font-mono text-text-grey">{Number.isFinite(expected.cutbackLengthMm) ? expected.cutbackLengthMm : '-'}</span> mm
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Coating results" subtitle="Values are calculated from saved coating readings and project limits" />
            {resultRows.map((row) => (
              <TensileResultRow
                key={row.label}
                label={row.label}
                formula={row.formula}
                value={row.value}
                unit={row.unit}
                min={row.min}
                max={row.max}
                lo={row.lo}
                hi={row.hi}
                status={rowStatus(row)}
              />
            ))}
          </CardContent>
        </Card>

        <div>
          <SectionHead title="Holiday test" subtitle={`Whole-pipe pinhole detection at ${holidayVoltageLimitText}`} />
          <Card className="border-[#E2E5EA]">
            <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-4">
              {[
                ['Voltage', Number.isFinite(expected.holidayVoltageKv) ? `${expected.holidayVoltageKv.toFixed(1)} kV` : '-'],
                ['Holidays found', Number.isFinite(expected.holidaysFound) ? expected.holidaysFound : '-', Number.isFinite(expected.holidaysFound) ? expected.holidaysFound === 0 ? 'PASS' : 'FAIL' : 'PENDING_REVIEW'],
                ['Cutback length', Number.isFinite(expected.cutbackLengthMm) ? `${expected.cutbackLengthMm} mm` : '-'],
                ['Surface prep', expected.surfacePrepGrade],
              ].map(([label, value, status]) => (
                <div key={String(label)} className="border-r border-[#E2E5EA] pr-4 last:border-r-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
                  <div className={cn('mt-2 font-mono text-[22px] font-semibold text-blueprint-blue', status === 'PASS' && 'text-galvanizing-green', status === 'FAIL' && 'text-readable-red')}>
                    {value}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Saved coating results" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">DFT Min</th>
                  <th className="px-4 py-3 text-right">Adhesion</th>
                  <th className="px-4 py-3 text-right">CD Radius</th>
                  <th className="px-4 py-3 text-right">Holidays</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {coatingMeasurements.map((measurement) => (
                  <tr key={measurement.id} onClick={() => setActiveCoatingId(measurement.id)} className={cn('cursor-pointer hover:bg-page-bg', activeCoating?.id === measurement.id && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3 font-mono font-semibold">{measurement.coatingType || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.coatingDftMinUm || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.coatingAdhesionNcm || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.coatingCdRadiusMm || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.coatingHolidaysFound || '-'}</td>
                    <td className="px-4 py-3"><StatusBadge status={measurement.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <ItemActionButtons measurement={measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Results', `${coatingMeasurements.length} saved`, true], ['Status', <StatusBadge key="status" status={overallStatus} />]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
